
import { defineComponent } from 'vue';

import { distance } from "@wwtelescope/astro";
import { Color, Folder, Layer, RenderContext, Settings} from "@wwtelescope/engine";
import { ImageSetType, MarkerScales, PlotTypes, PointScaleTypes, Thumbnail } from "@wwtelescope/engine-types";

import L, { LeafletMouseEvent, Map } from "leaflet";
import { tween } from "femtotween";
import { MiniDSBase, BackgroundImageset, skyBackgroundImagesets} from "@minids/common";

import { ImageSetLayer, Place, Imageset } from "@wwtelescope/engine";
import { applyImageSetLayerSetting } from "@wwtelescope/engine-helpers";

import { ref, onMounted } from 'vue';

interface MoveOptions {
  instant?: boolean;
  zoomDeg?: number;
  rollRad?: number;
}

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;
const hms2R = (360 * Math.PI) / 4320; // First convert to degrees and then to radians: ((1 / 24) * 360) * (Math.PI / 180);

type LocationRad = {
  longitudeRad: number;
  latitudeRad: number;
};

type EquatorialRad = {
  raRad: number;
  decRad: number;
};

type HorizontalRad = {
  altRad: number;
  azRad: number;
};

interface PointData {
  x: number;
  y: number;
  ra: number;
  dec: number;
  name: string;
  color: Color;
}

type ToolType = "crossfade" | "choose-background" | null;
type SheetType = "text" | "video" | null;

export default defineComponent({

  setup() {
    const fullscreen = ref(false);

    function toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.log(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
        });
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      }
    }

    onMounted(() => {
      document.addEventListener('fullscreenchange', () => {
        fullscreen.value = !!document.fullscreenElement;
      });
    });

    return {
      fullscreen,
      toggleFullscreen
    };
  },

  extends: MiniDSBase,
  name: 'MyComponent',
  
  props: {
    wtml: {
      type: Object,
      required: true
    },
    wwtNamespace: {
      type: String,
      required: true
    },
    url: {
      type: String,
      required: true
    },
    thumbnailUrl: {
      type: String,
      required: true
    },
    bgWtml: {
      type: String,
      required: true
    },
    bgName: {
      type: String,
      required: true
    },
    fgName: {
      type: String,
      required: true
    },
    title: {
      required: true
    },
    introText: {
      required: true
    },
    introTitle: {
      required: true
    },
    introInstructions: {
      required: true
    },
  },
  
  data() {
    return {
      isMenuOpen: false,
      hiddenTitle: false,
      hiddenIntro: false,
      showIntro: true,
      showBgButton: false,
      showBgSlider: true,
      showFolderView: true,
      descriptions: '',
      descrTitles: '',
      descrCredits: '',
      descrCreditsURL: '',
      showDescription: false,
      showLeftMenu: false,
      showTitle: true,
 
      showSplashScreen: true,
      imagesetLayers: {} as Record<string, ImageSetLayer>,
      layersLoaded: false,
      positionSet: false,
      imagesetFolder: null as Folder | null,
      backgroundImagesets: [] as BackgroundImageset[],
      layers: {} as Record<string,ImageSetLayer>,

      color0: "#4c8bff",
      color1: "#00b27a",
      color2: "#ffaa00",
      color3: "#8901bd",
      color4: "#00c9ff",
      color5: "#ff0000",
      colorD: "#45aecb",
      colorRed: "#ffffff",
      todayColor: "#D6B004",

      incomingItemSelect: null as Thumbnail | null,
      sheet: null as SheetType,
      showTextTooltip: false,
      showVideoTooltip: false,
      showControls: false,
      currentTool: "crossfade" as ToolType,
      tab: 0,
      map: null as Map | null,
      lastClosePt: null as PointData | null
    };
  },

  mounted() {

    this.waitForReady().then(async () => {
      this.setClockSync(false);
      this.imagesetFolder = await this.loadImageCollection({
        url: this.wtml.layeredimagery,
        loadChildFolders: false
      });
      const children = this.imagesetFolder.get_children() ?? [];
      const layerPromises: Promise<Layer>[] = [];

      children.reverse().forEach((item) => {
        if (!(item instanceof Place)) { return; }
        const imageset = item.get_backgroundImageset() ?? item.get_studyImageset();
        if (imageset == null) { return; }
        const name = imageset.get_name();
        layerPromises.push(this.addImageSetLayer({
          url: imageset.get_url(),
          mode: "autodetect",
          name: name,
          goto: false
        }).then((layer) => {
          this.imagesetLayers[name] = layer;
          applyImageSetLayerSetting(layer, ["opacity", 1]); // Here is where you set your initial image's opacity to either 0 or 1 (0 being invisible).
          return layer;
        }));
      });
      
      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.curForegroundImagesetName = this.fgName;
        this.curBackgroundImagesetName = this.bgName;
        this.foregroundOpacity = 0;

        tween(0, 50, (value) => { this.foregroundOpacity = value; }, {
          time: 7500,
          //ease: (t: number) => t * (2 * (1 - t) * 1.75 + t * 0.5),
          //done: () => { this.foregroundOpacity = 50; }
        });
      });
      
      
      // this.backgroundImagesets = [...skyBackgroundImagesets]; // This is the default background imagery

      this.showControls = !this.mobile;

      /* This section adds extra foreground imagery to the default sky survey menu. 
      This is hard coded and we could find a better way to add them in the future. */
      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("SFD Dust Map (Infrared)", "SFD Dust Map (Infrared)") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("2Mass (Infrared)", "2Mass: Imagery (Infrared)") 
        );
      });      

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("WISE (Infrared)", "WISE All Sky (Infrared)") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("unWISE (Infrared)", "unWISE color, from W2 and W1 bands") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("PanSTARRS1 3pi (Optical) ", "PanSTARRS1 3pi") 
        );
      });
      
      // this.loadImageCollection({
      //   url: this.bgWtml,
      //   loadChildFolders: true,
      // }).then((folder) => {
      //   this.backgroundImagesets.unshift(
      //     new BackgroundImageset("Digitized Sky Survey (Optical)", "Digitized Sky Survey (Color)") 
      //   );
      // });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("USNOB (Optical)", "USNOB: US Naval Observatory B 1.0 (Synthetic, Optical)") // First name is what you want the menu to look like, second name should be what you see in your WWT collection.
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("Gaia DR2 (Optical)", "Gaia DR2") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("NASA Deep Star Maps (Optical)", "Deep Star Maps 2020") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("GALEX (Ultraviolet)", "GALEX (Ultraviolet)") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("ROSAT (X-ray)", "RASS: ROSAT All Sky Survey (X-ray)") 
        );
      });      

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("Fermi (Gamma)", "Fermi LAT 8-year (gamma)") 
        );
      });

      Promise.all(layerPromises).then(() => {
        this.layersLoaded = true;
        
        // Set all of the imageset layers to be above the spreadsheet layers
        //this.resetImagesetLayerOrder();

        const splashScreenListener = (_event: KeyboardEvent) => {
          this.showSplashScreen = false;
          window.removeEventListener('keyup', splashScreenListener);
        };
        window.addEventListener('keyup', splashScreenListener);

        window.addEventListener('keyup', (event: KeyboardEvent) => {
          if (["Esc", "Escape"].includes(event.key) && this.showVideoSheet) {
            this.showVideoSheet = false;
          }
        });
      });

      // wwtZoomDeg is still 0 if we run this here
      // and it was the same in nextTick
      // so give just a bit of a delay
      setTimeout(() => {
        this.positionSet = true;
      }, 100);

    });

  },

  
  computed: {

    // shouldShowIntro() {
    //   return !this.showIntro;
    // },
    
    isLoading(): boolean {
      return !this.ready;
    },
    ready(): boolean {
      return this.layersLoaded && this.positionSet;
    },
    smallSize(): boolean {
      return this.$vuetify.display.smAndDown;
    },
    mobile(): boolean {
      return this.smallSize && this.touchscreen;
    },
    cssVars() {
      return {
        '--color-default': this.colorD,
        '--color-default2': this.colorRed,
				'--color0':this.color0,
				'--color1':this.color1,
				'--color2':this.color2,
				'--color3':this.color3,
				'--color4':this.color4,
				'--color5':this.color5,
        '--app-content-height': this.showTextSheet ? '66%' : '100%',
      };
    },
    popoverCssVars() {
      const x = this.lastClosePt?.x ?? 0;
      const y = this.lastClosePt?.y ?? 0;

      // Note that the canvas takes up the full width
      const leftPct = 100 * x / window.innerWidth;
      const topPct = 100 * (y - 50) / window.innerHeight;
      return {
        '--current-color': this.lastClosePt?.color.toString() ?? "white",
        '--current-left': `${leftPct}%`,
        '--current-top': `${topPct}%`
      };
    },

    wwtSettings(): Settings {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return Settings.get_active();
    },
 
    
    showTextSheet: {
      get(): boolean {
        return this.sheet === 'text';
      },
      set(_value: boolean) {
        this.selectSheet('text');
        this.showTextTooltip = false;
      }
    },
    showVideoSheet: {
      get(): boolean {
        return this.sheet === "video";
      },
      set(value: boolean) {
        this.selectSheet('video');
        if (!value) {
          const video = document.querySelector("#info-video") as HTMLVideoElement;
          video.pause();
        }
        this.showVideoTooltip = false;
      }
    },
    
    curBackgroundImagesetName: {
      get(): string {
        if (this.wwtBackgroundImageset == null) return "";
        return this.wwtBackgroundImageset.get_name();
      },
      set(name: string) {
        this.setBackgroundImageByName(name);
      }
    },
    
    curForegroundImagesetName: {
      get(): string {
        if (this.wwtForegroundImageset == null) return "";
        return this.wwtForegroundImageset.get_name();
      },
      set(name: string) {
        this.setForegroundImageByName(name);
      }
    },


    foregroundOpacity: {
      get(): number {
        return this.wwtForegroundOpacity;
      },
      set(o: number) {
        this.setForegroundOpacity(o); 
      }
    },
  },

  methods: {

    // toggleLeftMenu() {
    //   this.showLeftMenu = !this.showLeftMenu;

    // },

    /* Toggles the hamburger menu when the hamburger icon is clicked */
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      this.showTitle = false;
      this.showFolderView = !this.showFolderView;
    },
    
    /* This is for the close button of the intro box */
    closeIntro() {
      this.showIntro = false;
    },
    
    /* This is for the "Toggle description" in the hamburger menu. It opens the intro container */
    openIntro() {
      this.showIntro = !this.showIntro;
      this.showBgButton = false;
      this.showDescription = false;
    },

    closeDescription() {
      this.showDescription = false;
    },

    /* Toggles the background button when the Choose background button is clicked */
    toggleBgButton() {
      this.showBgButton = !this.showBgButton;
      this.hiddenIntro = !this.hiddenIntro;
      this.showIntro = false;
      this.showDescription = false;
      this.showBgSlider = false;
      this.showFolderView = true;
      this.isMenuOpen = false;
    },    

    toggleBgSlider() {
      this.showBgSlider = !this.showBgSlider;
      this.showIntro = false;
      this.showDescription = false;
      this.showBgButton = false;
      this.showFolderView = true;
      this.isMenuOpen = false;
    },

    hideMenu() {
    this.showBgButton = false;
    this.showBgSlider = true;
    },

    toggleFolderView() {
      this.showIntro = false;
      this.showDescription = true;
    },

    hideTitle() {
      this.showTitle = false;
    },

    closeSplashScreen() {
      this.showSplashScreen = false;
    },

    selectSheet(name: SheetType) {
      if (this.sheet == name) {
        this.sheet = null;
      } else {
        this.sheet = name;
      }
    },

    getImageSetLayerIndex(layer: ImageSetLayer): number {
      // find layer in this.wwtActiveLayers
      // this.wwtActiveLayers is a dictionary of {0:id1, 1:id2, 2:id3, ...}
      // get the key item with the value of layer.id
      for (const [key, value] of Object.entries(this.wwtActiveLayers)) {
        if (value === layer.id.toString()) {
          return Number(key);
        }
      }
      return -1;
    },




    setLayerOpacityForImageSet(name: string, opacity: number, setting_opacity_from_ui=false) {
      const layer = this.imagesetLayers[name];
      if (layer != null) {
        // update the image opacity in the WWT control
        applyImageSetLayerSetting(layer, ['opacity', opacity]);

        // update the value for the slider only if we are not setting the opacity from the UI
        if (!setting_opacity_from_ui) {
          const selector = `#items div.item[title='${name}'] input.opacity-range[type='range']`;
          const el = (document.querySelector(selector) as HTMLInputElement);
          if (el != null) {
            el.value = `${opacity * 100}`;
          }
        }

        const toggleSelector = `#items input[type='checkbox'][title='${name}']`;
        const el2 = (document.querySelector(toggleSelector) as HTMLInputElement);
        // truth table: opacity == 0 and el.checked == false => do nothing
        // truth table: opacity == 0 and el.checked == true => set el.checked = false
        // truth table: opacity > 0 and el.checked == false => set el.checked = true
        // truth table: opacity > 0 and el.checked == true => do nothing
        if (el2 != null) {
          if (opacity == 0 && el2.checked) {
            el2.checked = false;
          } else if (opacity > 0 && !el2.checked) {
            el2.checked = true;
          }
        }
        
      }
    },
		
		onItemSelected(place: Place) {
      //this.logTimes(`onItemSelected: ${place.get_name()}`)
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset == null) { return; }
      const layer = this.imagesetLayers[iset.get_name()];

      // Give time for the selectedTime changes to propagate
      setTimeout(() => {
        this.$nextTick(() => {
          //if (this.image_out_of_view(place) || this.need_to_zoom_in(place)) {
            this.descriptions = place.htmlDescription;
            this.descrTitles = iset.get_name(); 
            this.descrCredits = iset.get_creditsText();
            this.descrCreditsURL = iset.get_creditsUrl();
            
            this.gotoRADecZoom({
              raRad: hms2R * place.get_RA(), // In the WTML file ra is in hours. Convert to radians (which is what hms2R does)
              decRad: D2R * place.get_dec(), // In the WTML file dec is in degrees. Convert to radians (which is what DR2 does)                
              // raRad: D2R * iset.get_centerX(), // We don't wanna use these lines because the will make the images skip
              // decRad: D2R * iset.get_centerY(),              
              zoomDeg: place.get_zoomLevel() * 1, // I don't think that this line is the issue with the skipping place
              instant: true
            }); 
        });
      }, 10);  
    },
		
		onOpacityChanged(place: Place, opacity: number, move: boolean) {
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset == null) { return; }
      this.updateImageOpacity(place, opacity);
      

      // this.$nextTick(() => {
      //     this.incomingItemSelect = place;
      //     this.gotoRADecZoom({
      //       raRad: D2R * iset.get_centerX(),
      //       decRad: D2R * iset.get_centerY(),
      //       zoomDeg: this.wwtZoomDeg, //zoom,
      //       instant: false
      //     });
      // });
		},
		
		updateImageOpacity(place: Place, opacity: number) {
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset == null) { return; }
      const layer = this.imagesetLayers[iset.get_name()];
      if (layer == null) { return; }
      // applyImageSetLayerSetting(layer, ["opacity", opacity / 100]);
      // tell setLayerOpacityForImageSet that we are updating from the ui
      this.setLayerOpacityForImageSet(iset.get_name(), opacity / 100, true);
    },
  },

  // watch: {

  //   yrqp(on: boolean) {
  //     if (this.yrqpLayer) {
  //       applySpreadSheetLayerSetting(this.yrqpLayer, ["opacity", on ? 1.0 : 0.0]);
  //     }
  //   },
  //   yrlp(on: boolean) {
  //     if (this.yrlpLayer) {
  //       applySpreadSheetLayerSetting(this.yrlpLayer, ["opacity", on ? 1.0 : 0.0]);
  //     }
  //   },
  //   imp(on: boolean) {
  //     if (this.impLayer) {
  //       applySpreadSheetLayerSetting(this.impLayer, ["opacity", on ? 1.0 : 0.0]);
  //     }
  //   },
  //   bmp(on: boolean) {
  //     if (this.bmpLayer) {
  //       applySpreadSheetLayerSetting(this.bmpLayer, ["opacity", on ? 1.0 : 0.0]);
  //     }
  //   },
  //   bws(on: boolean) {
  //     if (this.bwsLayer) {
  //       applySpreadSheetLayerSetting(this.bwsLayer, ["opacity", on ? 1.0 : 0.0]);
  //     }
  //   },    
  //   rs(on: boolean) {
  //     if (this.rsLayer) {
  //       applySpreadSheetLayerSetting(this.rsLayer, ["opacity", on ? 1.0 : 0.0]);
  //     }
  //   }    
  // }
	

	
});

